import React, { FC, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import confirm from 'modules/confirm';
import { SUCCESS_MESSAGES } from 'request-responses';
import api from 'api';
import XcircleIcon from 'icons/Xcircle.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { BUYING_PORTFOLIO_ROUTE } from 'data/global';
import mixpanelService from 'services/mixpanelService';
import { EnquiryWithdrawalInitiatedEventProps } from 'models/MixpanelEvents';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';

import './WithdrawButton.styles.scss';

type WithdrawButtonProps = {
  className?: string;
  withStyle?: boolean;
  isLoading: boolean;
  leadId: string | null;
  dealId: string;
  withdrawText?: string;
  setIsLoading: (value: boolean) => void;
  onSuccess?: () => void;
};

const WithdrawButton: FC<WithdrawButtonProps> = ({
  className,
  isLoading,
  leadId,
  dealId,
  withdrawText,
  withStyle = true,
  setIsLoading,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames({ 'withdraw-button': withStyle }, className);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const handleWithdraw = useCallback(
    async (e) => {
      e.stopPropagation();

      if (
        await confirm(
          {
            title: t('withdrawInterestTitle'),
            content: t('withdrawInterestDescription'),
            type: 'delete',
            submitBtnText: t('yesWithdrawInterest'),
            cancelBtnText: t('cancelWithdrawal'),
          },
          true,
        )
      ) {
        try {
          setIsLoading(true);

          mixpanelService.enquiryWithdrawalInitiated(
            getMixpanelProps({
              leadId,
              dealId,
            }) as EnquiryWithdrawalInitiatedEventProps,
          );

          await api.portfolio.buyer.withdrawInterest(leadId);

          onSuccess?.();
          navigate(BUYING_PORTFOLIO_ROUTE, { replace: true });
          popSuccess(t(SUCCESS_MESSAGES.WITHDRAWN_INTEREST));
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      leadId,
      dealId,
      getMixpanelProps,
      navigate,
      onSuccess,
      popServerError,
      popSuccess,
      setIsLoading,
      t,
    ],
  );

  if (!leadId) return null;

  return (
    <Button
      className={classes}
      variant="link"
      onClick={handleWithdraw}
      icon={XcircleIcon}
      disabled={isLoading}
    >
      {withdrawText || t('withdrawInterest')}
    </Button>
  );
};

export default WithdrawButton;
